import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Sentry from 'Integrations/Sentry';
import { useMaybeUserContext } from 'Context/UserContext';
import { UserReportOverride } from 'Overrides/UserReport';
import logEventActions from 'EventTracking/useEventTrackingLogEvent/logEventActions';
import isDev from 'Helpers/isDev';
import {
  FDBox,
  FDBodyText,
  FDContentContainer,
  FDStack,
  FDAlert,
  FDHeadlineText,
  FDPaper,
} from '@facet-wealth/design';
import { BoneBackground } from 'Errors/BoneBackground';
import { MetaResourceRendererLite } from 'Components/UI/Meta/MetaForm/Components/MetaResourceRendererLite';
import { UserReportLayout } from 'Errors/UserReportLayout';
import Receptacle from '../Icons/Receptacle';

export const ErrorMessage = () => (
  <>
    <Receptacle style={{ width: '6em', height: '6em' }} />
    <FDHeadlineText align="center" value={'Oops!  Something came unplugged.  Please reload.'} color="textSecondary" />
  </>
);

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      sentryEventId: null,
    };
  }

  static getDerivedStateFromError() {
    // this function takes an error as a arg, we could pull a message out of the error to display to a user
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error: true };
  }

  componentDidCatch(error, errorInfo) {
    if (isDev()) {
      console.error('ErrorBoundary caught an error', error, errorInfo?.componentStack); // eslint-disable-line no-console -- this is used for blowing up tests whenever we hit an unplug
    }
    const sentryEventId = Sentry.createSentryErrorLog(error, undefined, { componentStack: errorInfo?.componentStack });
    this.setState({ sentryEventId, error });
  }

  render() {
    const { hasError, sentryEventId, error } = this.state;
    const { children } = this.props;

    if (hasError) {
      return (
        <FDBox padding={400}>
          <BoneBackground />
          <FDContentContainer maxWidth={'sm'}>
            <FDStack
              spacing={4}
              direction={'column'}
              alignItems={'center'}
              justifyContent="flex-start"
            >
              <ErrorMessage />
              <ReportIssueForm sentryEventId={sentryEventId} />
              {isDev() ? (
                <FDPaper>
                  <FDBodyText value={`[Dev Only]: ${error.name}`} />
                  <FDStack
                    spacing={2}
                    direction={'column'}
                    alignItems={'center'}
                    justifyContent="flex-start"
                  >
                    <FDBodyText value={error.message ?? 'No error message'} />
                    <FDBodyText value={error.stack ?? 'No stack trace'} />
                  </FDStack>
                </FDPaper>
              ) : null}
            </FDStack>
          </FDContentContainer>
        </FDBox>
      );
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.any.isRequired,
};

export const ReportIssueForm = ({ sentryEventId }) => {
  const userResource = useMaybeUserContext()?.userResource;
  const [userReportResource, setUserReportResource] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    logEventActions.logEvent('Unplugged Screen');
  }, []);

  useEffect(() => {
    async function getUserReportResource() {
      if (userResource && userResource.relationship('userReport')) {
        const collection = await userResource.relationship('userReport').link('related').Get({ metaOverrideSet: UserReportOverride });
        const resource = collection.create({ metaOverrideSet: UserReportOverride })
          .attr('sentryEventId', sentryEventId);

        setUserReportResource(resource);
      }
    }

    getUserReportResource();
  }, [userResource, sentryEventId]);

  const handleSubmit = () => {
    setSubmitted(true);
  };

  if (!userReportResource) return null;
  return (
    <>
      {submitted
        ? (
          <FDAlert
            status="warning"
            title="Your report was submitted successfully."
            description={'The team has been contacted. Thank you for the report.'}
          />
        ) : (
          <MetaResourceRendererLite
            layout={UserReportLayout}
            resource={userReportResource}
            onSubmit={handleSubmit}
          />
        )}
    </>
  );
};

ReportIssueForm.propTypes = {
  sentryEventId: PropTypes.string.isRequired,
};
